import React, { useEffect, useRef } from "react";

interface MenuSurfaceProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  align?: "left" | "right";
  /** Rendered inside the trigger button. */
  trigger: React.ReactNode;
  triggerClassName?: string;
  triggerTitle?: string;
  children: React.ReactNode;
  panelClassName?: string;
}

/**
 * The dropdown shell both header menus share.
 *
 * It exists because the outside-click listener is the part that gets subtly
 * wrong when copied: AppHeader had one bound for the lifetime of the
 * component whether or not the menu was open. Here it is bound only while
 * open, and Escape returns focus to the trigger so keyboard users are not
 * dropped at the top of the document.
 */
const MenuSurface: React.FC<MenuSurfaceProps> = ({
  open,
  onOpenChange,
  align = "left",
  trigger,
  triggerClassName = "",
  triggerTitle,
  children,
  panelClassName = "",
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      onOpenChange(false);
      triggerRef.current?.focus();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={triggerTitle}
        className={triggerClassName}
      >
        {trigger}
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute top-full mt-1.5 z-50 min-w-[250px] p-1.5 rounded-lg bg-surface-container border border-outline-variant/60 shadow-2xl shadow-black/60 ${
            align === "right" ? "right-0" : "left-0"
          } ${panelClassName}`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

/** A row inside a MenuSurface panel. */
export const MenuRow: React.FC<{
  icon?: string;
  onClick?: () => void;
  danger?: boolean;
  active?: boolean;
  meta?: string;
  children: React.ReactNode;
}> = ({ icon, onClick, danger = false, active = false, meta, children }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left font-label text-[12.5px] transition-colors ${
      active ? "text-primary" : "text-slate-400"
    } ${danger ? "hover:text-error" : "hover:text-on-surface"} hover:bg-surface-container-high`}
  >
    {icon && <span className="material-symbols-outlined text-[17px]">{icon}</span>}
    <span className="flex-1 min-w-0 truncate">{children}</span>
    {meta && <span className="text-[10.5px] text-slate-500 tabular-nums shrink-0">{meta}</span>}
  </button>
);

export const MenuRule: React.FC = () => (
  <div className="h-px bg-outline-variant/45 my-1.5 mx-1" />
);

export default MenuSurface;
